/**
 * RunDeltaStrip — Operational delta and freshness strip for the project overview.
 * Replaces static inventory counters and bureaucratic quality gate cards with
 * high-signal run-over-run deltas: verified fixes, reopened regressions, new blockers,
 * and deployment provenance.
 */
import { Link } from "react-router-dom";
import { trpc } from "@/lib/trpc";
import { Chip } from "@/components/ui/chip";
import { Timestamp } from "@/components/ui/timestamp";
import { Skeleton } from "@/components/ui/skeleton";
import {
  GateStatusChip,
  gateStatusLabel,
} from "@/components/gate/gate-vocabulary";
import type { GateHistoryView } from "@/components/gate/gate-data";
import {
  Check,
  AlertTriangle,
  Plus,
  RefreshCw,
  GitBranch,
  Clock,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { cn } from "@/lib/utils";

const HEX_SHA_RE = /^[0-9a-f]{12,40}$/i;
const STATUS_PREFIX_RE =
  /^(inconclusive|passed with warnings|passed|failed|error):\s*/i;

export function formatBuildLabel(label: string): string {
  if (label.includes("+")) {
    const [prefix] = label.split("+");
    return HEX_SHA_RE.test(prefix) ? `${prefix.slice(0, 7)}+` : `${prefix}+`;
  }
  return HEX_SHA_RE.test(label) ? label.slice(0, 7) : label;
}

export function stripGateStatusPrefix(headline: string): string {
  const cleaned = headline.replace(STATUS_PREFIX_RE, "");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function buildDotTone(status: GateHistoryView["status"]): string {
  switch (status) {
    case "passed":
      return "bg-mark-helps border-mark-helps/60";
    case "warned":
    case "inconclusive":
      return "bg-mark-important border-mark-important/60";
    case "failed":
    case "error":
      return "bg-mark-critical border-mark-critical/60";
    default:
      return "bg-muted-foreground/40 border-border";
  }
}

export function RunDeltaStrip({
  projectId,
  awaitingRerunCount = 0,
  className,
}: {
  projectId: string;
  awaitingRerunCount?: number;
  className?: string;
}) {
  const { data, isLoading, isError } = trpc.gate.history.useQuery({
    projectId,
    limit: 10,
  });

  if (isLoading) {
    return (
      <div
        id="run-delta-strip"
        className="rounded-lg border border-border/80 bg-card p-3 shadow-2xs"
        aria-label="Loading run delta"
      >
        <Skeleton className="h-6 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        id="run-delta-strip"
        className="flex flex-col gap-2 rounded-lg border border-dashed border-border/80 bg-card/60 px-3.5 py-2.5 text-xs sm:flex-row sm:items-center sm:justify-between"
        role="region"
        aria-label="Run delta and freshness"
      >
        <span className="text-xs text-muted-foreground">
          Run delta unavailable
        </span>
      </div>
    );
  }

  const history = (data ?? []) as unknown as GateHistoryView[];
  const latest = history[0];

  if (!latest) {
    return (
      <div
        id="run-delta-strip"
        className="flex flex-col gap-2 rounded-lg border border-dashed border-border/80 bg-card/60 px-3.5 py-2.5 text-xs sm:flex-row sm:items-center sm:justify-between"
        role="region"
        aria-label="Run delta and freshness"
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground">
          <span className="font-medium text-foreground">
            No baseline evaluated yet
          </span>
          <span className="hidden sm:inline select-none" aria-hidden="true">
            ·
          </span>
          <span>
            Baselines are established once walkthrough runs are completed.
          </span>
        </div>
        <Link
          to={`/projects/${projectId}/scenarios`}
          className="font-medium text-primary hover:underline"
        >
          View scenarios →
        </Link>
      </div>
    );
  }

  const fixedTotal =
    latest.counts.fixed.critical +
    latest.counts.fixed.major +
    latest.counts.fixed.minor;

  const reopenedTotal =
    latest.counts.fix_didnt_hold.critical +
    latest.counts.fix_didnt_hold.major +
    latest.counts.fix_didnt_hold.minor +
    latest.counts.regressed.critical +
    latest.counts.regressed.major +
    latest.counts.regressed.minor;

  const newTotal =
    latest.counts.new.critical +
    latest.counts.new.major +
    latest.counts.new.minor;

  const newCriticalMajor = latest.counts.new.critical + latest.counts.new.major;

  const improvedVerdicts = latest.verdicts?.improved ?? 0;
  const declinedVerdicts =
    (latest.verdicts?.declined ?? 0) + (latest.verdicts?.declined_to_no ?? 0);
  const chronologicalHistory = [...history].reverse();

  return (
    <div
      id="run-delta-strip"
      className={cn(
        "flex flex-col gap-3 rounded-lg border border-border/80 bg-card p-3 shadow-2xs lg:flex-row lg:items-center lg:justify-between",
        className,
      )}
      role="region"
      aria-label="Quality gate and build freshness"
    >
      {/* Left: Gate Verdict, Headline & Build Freshness Provenance */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-xs">
        <Link
          to={`/projects/${projectId}/walkthroughs/builds/${latest.buildId}`}
          className="hover:opacity-90 transition-opacity"
          title={`View build ${latest.label} gate details`}
        >
          <GateStatusChip status={latest.status} />
        </Link>
        <span
          className="inline-flex max-w-[220px] items-center gap-1 rounded bg-muted/60 px-1.5 py-0.5 font-medium text-xs text-foreground"
          title={latest.environment || "local"}
        >
          <GitBranch
            className="size-3 text-muted-foreground shrink-0"
            aria-hidden="true"
          />
          <span className="truncate">{latest.environment || "local"}</span>
        </span>
        <Link
          to={`/projects/${projectId}/walkthroughs/builds/${latest.buildId}`}
          className="font-mono text-xs text-foreground font-medium whitespace-nowrap hover:underline underline-offset-4"
        >
          {formatBuildLabel(latest.label)}
          {latest.gitBranch ? ` (${latest.gitBranch})` : ""}
        </Link>
        <span className="text-muted-foreground select-none" aria-hidden="true">
          ·
        </span>
        <span className="inline-flex items-center gap-1 text-muted-foreground whitespace-nowrap">
          <Clock
            className="size-3 text-muted-foreground shrink-0"
            aria-hidden="true"
          />
          <Timestamp date={latest.lastRunAt ?? latest.evaluatedAt} />
        </span>
        {latest.headline && (
          <span className="text-muted-foreground">
            {stripGateStatusPrefix(latest.headline)}
          </span>
        )}
      </div>

      {/* Right: Operational Deltas & Build Trajectory */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {fixedTotal > 0 && (
          <Link
            to={`/projects/${projectId}/walkthroughs/builds/${latest.buildId}`}
            title={`${fixedTotal} problems verified resolved in this run`}
          >
            <Chip tone="helps" className="hover:opacity-90 transition-opacity">
              <Check className="size-3" aria-hidden="true" />+{fixedTotal}{" "}
              verified fixed
            </Chip>
          </Link>
        )}

        {reopenedTotal > 0 && (
          <Link
            to={`/projects/${projectId}/findings?view=triage`}
            title={`${reopenedTotal} regressions or fixes that failed to hold`}
          >
            <Chip
              tone="critical"
              className="hover:opacity-90 transition-opacity font-semibold"
            >
              <AlertTriangle className="size-3" aria-hidden="true" />
              {reopenedTotal} reopened / regressed
            </Chip>
          </Link>
        )}

        {newTotal > 0 && (
          <Link
            to={`/projects/${projectId}/findings?view=triage`}
            title={`${newTotal} new problems discovered`}
          >
            <Chip
              tone="important"
              className="hover:opacity-90 transition-opacity"
            >
              <Plus className="size-3" aria-hidden="true" />+{newTotal} new{" "}
              {newCriticalMajor > 0
                ? `(${newCriticalMajor} major/critical)`
                : "issues"}
            </Chip>
          </Link>
        )}

        {improvedVerdicts > 0 && (
          <Chip
            tone="helps"
            title={`${improvedVerdicts} human test verdicts improved`}
          >
            <TrendingUp className="size-3" aria-hidden="true" />+
            {improvedVerdicts}{" "}
            {improvedVerdicts === 1 ? "journey improved" : "journeys improved"}
          </Chip>
        )}

        {declinedVerdicts > 0 && (
          <Chip
            tone="critical"
            title={`${declinedVerdicts} human test verdicts declined`}
          >
            <TrendingDown className="size-3" aria-hidden="true" />-
            {declinedVerdicts}{" "}
            {declinedVerdicts === 1 ? "journey declined" : "journeys declined"}
          </Chip>
        )}

        {awaitingRerunCount > 0 && (
          <Link
            to={`/projects/${projectId}/findings?view=awaiting_rerun`}
            title={`${awaitingRerunCount} fixes waiting for a re-run to verify`}
          >
            <Chip
              tone="outline"
              className="hover:opacity-90 transition-opacity"
            >
              <RefreshCw className="size-3" aria-hidden="true" />
              {awaitingRerunCount} awaiting re-run
            </Chip>
          </Link>
        )}

        {latest.status === "passed" &&
          fixedTotal === 0 &&
          reopenedTotal === 0 &&
          newTotal === 0 && (
            <Chip tone="neutral">
              <Check className="size-3 text-finding-helps" aria-hidden="true" />
              No regressions vs baseline
            </Chip>
          )}

        {chronologicalHistory.length > 0 && (
          <div
            className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-2 py-1"
            role="group"
            aria-label={`Recent build trend (${chronologicalHistory.length} builds, oldest to newest)`}
          >
            <span className="text-muted-foreground">
              Last {chronologicalHistory.length}{" "}
              {chronologicalHistory.length === 1 ? "build" : "builds"}:
            </span>
            <div className="inline-flex items-center gap-1">
              {chronologicalHistory.map((b) => {
                const statusLabel = gateStatusLabel(b.status);
                const summary = `${formatBuildLabel(b.label)}: ${statusLabel}${b.headline ? ` — ${b.headline}` : ""}`;
                return (
                  <Link
                    key={b.id}
                    to={`/projects/${projectId}/walkthroughs/builds/${b.buildId}`}
                    aria-label={summary}
                    title={summary}
                    className={cn(
                      "size-2.5 rounded-xs border transition-transform hover:scale-125",
                      buildDotTone(b.status),
                    )}
                  />
                );
              })}
            </div>
          </div>
        )}

        <Link
          to={`/projects/${projectId}/walkthroughs/builds`}
          className="ml-1 text-xs font-medium text-primary hover:underline"
        >
          All builds →
        </Link>
      </div>
    </div>
  );
}
